import { Component, ChangeDetectionStrategy, ChangeDetectorRef, inject, signal, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, FormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { PERMISSION_UI_DIRECTIVES } from '../../directives/permission.directive';
import { InputTextModule } from 'primeng/inputtext';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TabsModule } from 'primeng/tabs';
import {
  CompanyService,
  CreateCompanyRequest,
  CompanyDTO,
  CompanyAddressDTO,
  CompanyContactDTO,
  CreateCompanyAddressRequest,
  CreateCompanyContactRequest
} from '../../services/company.service';
import {
  SerieService,
  SerieDTO,
  SerieType,
  CreateSerieRequest,
  UpdateSerieRequest
} from '../../services/serie.service';
import { CountryService } from '../../services/country.service';
import type { CountryDTO } from '../../services/country.service';
import { AddressService } from '../../services/address.service';
import { AuthService } from '../../services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { finalize, take } from 'rxjs';
import { InputMaskModule } from 'primeng/inputmask';
import { InputNumberModule } from 'primeng/inputnumber';
import { TagModule } from 'primeng/tag';
import { TaxRegimeService } from '../../services/tax-regime.service';
import type { TaxRegimeDTO } from '../../services/tax-regime.service';
import { CompanyTypeEnum, CompanyTypeOptions } from '../../core/models';
import { TooltipModule } from 'primeng/tooltip';
import { CompanyEmailSignaturesComponent } from './company-email-signatures.component';

@Component({
  selector: 'app-company-form',
  templateUrl: './company-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host ::ng-deep .sale-form-card.p-card .p-card-header {
        padding: 0;
      }
      :host ::ng-deep .sale-form-card.p-card .p-card-body {
        padding-top: 0.25rem;
      }
      :host ::ng-deep .sale-form-card .p-tablist,
      :host ::ng-deep .sale-form-card .p-tabview-tablist,
      :host ::ng-deep .sale-form-card .p-tabs-nav,
      :host ::ng-deep .sale-form-card [data-pc-section='tablist'] {
        padding-top: 0;
        margin-top: 0;
      }
      :host ::ng-deep .sale-form-card .p-tabpanels,
      :host ::ng-deep .sale-form-card .p-tabpanel {
        padding-top: 0.5rem;
      }
    `
  ],
  imports: [
    CommonModule,
    PERMISSION_UI_DIRECTIVES,
    ReactiveFormsModule,
    InputTextModule,
    FloatLabelModule,
    SelectModule,
    ButtonModule,
    CardModule,
    TabsModule,
    InputMaskModule,
    InputNumberModule,
    FormsModule,
    TagModule,
    TooltipModule,
    CompanyEmailSignaturesComponent
  ]
})
export class CompanyFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private companyService = inject(CompanyService);
  private serieService = inject(SerieService);
  private countryService = inject(CountryService);
  private addressService = inject(AddressService);
  private taxRegimeService = inject(TaxRegimeService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private cdr = inject(ChangeDetectorRef);

  companyForm: FormGroup;
  loading = signal(false);
  initialLoad = signal(true);
  error = signal<string | null>(null);
  isEditMode = signal(false);
  editingCompanyId = signal<number | null>(null);
  addresses = signal<CompanyAddressDTO[]>([]);
  contacts = signal<CompanyContactDTO[]>([]);
  series = signal<SerieDTO[]>([]);
  addressForm: FormGroup;
  contactForm: FormGroup;
  serieForm: FormGroup;
  editingAddressId = signal<number | null>(null);
  editingContactId = signal<number | null>(null);
  editingSerieId = signal<number | null>(null);
  showAddressForm = signal(false);
  showContactForm = signal(false);
  showSerieForm = signal(false);
  addressModalShown = signal(false);
  contactModalShown = signal(false);
  serieModalShown = signal(false);
  relationsLoading = signal(false);
  seriesLoading = signal(false);
  deleteConfirmOpen = signal(false);
  deleteConfirmShown = signal(false);
  deleteTarget = signal<'address' | 'contact' | 'serie' | null>(null);
  deleteAddressItem = signal<CompanyAddressDTO | null>(null);
  deleteContactItem = signal<CompanyContactDTO | null>(null);
  deleteSerieItem = signal<SerieDTO | null>(null);
  deleteLoading = signal(false);

  // === Logo upload ===
  logoPreviewUrl = signal<string | null>(null);
  logoBase64 = signal<string | null>(null);
  logoContentType = signal<string>('image/jpeg');

  readonly CompanyTypeEnum = CompanyTypeEnum;
  readonly companyTypes = CompanyTypeOptions;

  addressTypes = [
    { value: 'BILLING', label: 'Facturación' },
    { value: 'PRIMARY', label: 'Principal' },
    { value: 'SHIPPING', label: 'Envío' },
    { value: 'CONTACT', label: 'Contacto' },
    { value: 'OTHER', label: 'Otro' }
  ];

  contactTypes = [
    { value: 'PRIMARY', label: 'Principal' },
    { value: 'OFFICE', label: 'Oficina' },
    { value: 'HOME', label: 'Casa' },
    { value: 'MOBILE', label: 'Móvil' },
    { value: 'FAX', label: 'Fax' },
    { value: 'EMAIL', label: 'Email' },
    { value: 'WEBSITE', label: 'Sitio Web' },
    { value: 'SOCIAL_MEDIA', label: 'Red Social' },
    { value: 'OTHER', label: 'Otro' }
  ];

  serieTypeOptions: { value: SerieType; label: string }[] = [
    { value: 'SALE', label: 'Venta' },
    { value: 'SUBSCRIPTION', label: 'Suscripción' },
    { value: 'INVOICE', label: 'Factura' },
    { value: 'SALE_LINK', label: 'Liga de pago' },
    { value: 'MIT_MANDATE', label: 'Mandato' }
  ];

  countries = signal<CountryDTO[]>([]);
  taxRegimeOptions = signal<TaxRegimeDTO[]>([]);
  coloniaOptions = signal<{ label: string; value: string }[]>([]);
  postalCodeLoading = signal(false);
  mexicoSelected = signal(true);
  municipalityDisplay = signal('');
  activeTab = signal<string>('generales');

  stateOptions = [
    { value: 'ACTIVE', label: 'Activo' },
    { value: 'INACTIVE', label: 'Inactivo' }
  ];

  readonly statusTooltip = 'Estado de la empresa: Activo o Inactivo.';

  readonly maxLength = {
    street: 200,
    municipality: 100,
    colonia: 100,
    externalNo: 30,
    internalNo: 30,
    zipCode: 5
  } as const;

  tabs = [
    { value: 'generales', label: 'Generales', icon: 'pi pi-building' },
    { value: 'terminos-pago', label: 'Términos pago', icon: 'pi pi-wallet' },
    { value: 'direcciones', label: 'Direcciones', icon: 'pi pi-map-marker' },
    { value: 'contactos', label: 'Contactos', icon: 'pi pi-users' },
    { value: 'series', label: 'Series', icon: 'pi pi-list' },
    { value: 'firmas', label: 'Firmas', icon: 'pi pi-pen-to-square' }
  ] as const;

  private static readonly nonNegativeIntegerValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
      return { nonNegativeInteger: true };
    }
    return null;
  };

  constructor() {
    this.companyForm = this.fb.group({
      type: [CompanyTypeEnum.COMPANY, Validators.required],
      taxRegimeId: [null as number | null, Validators.required],
      taxId: ['', [Validators.required, Validators.maxLength(50)]],
      legalName: ['', [Validators.required, Validators.maxLength(200)]],
      comercialName: ['', [Validators.required, Validators.maxLength(200)]],
      firstName: ['', [Validators.maxLength(150)]],
      lastName: ['', [Validators.maxLength(150)]],
      countryCode: [null as string | null, Validators.required],
      email: ['', [Validators.email, Validators.maxLength(100)]],
      phone: ['', [Validators.maxLength(20)]],
      state: ['ACTIVE'],
      daysUntilExpiry: [null as number | null, [CompanyFormComponent.nonNegativeIntegerValidator]]
    });

    this.addressForm = this.fb.group({
      addressType: ['BILLING', Validators.required],
      addressCountry: ['', [Validators.required, Validators.maxLength(5)]],
      street: ['', [Validators.required, Validators.maxLength(200)]],
      zipCode: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      municipality: ['', [Validators.required, Validators.maxLength(100)]],
      colonia: ['', [Validators.required, Validators.maxLength(100)]],
      externalNo: ['', [Validators.required, Validators.maxLength(30)]],
      internalNo: ['', [Validators.maxLength(30)]]
    });

    this.contactForm = this.fb.group({
      contactType: ['BILLING', Validators.required],
      conactName: ['', [Validators.required, Validators.maxLength(100)]],
      contactEmail: ['', [Validators.email, Validators.maxLength(100)]],
      contactPhone: ['', [Validators.maxLength(14)]]
    });

    this.serieForm = this.fb.group({
      type: [null as SerieType | null, [Validators.required]],
      serie: ['', [Validators.maxLength(5)]],
      description: ['', [Validators.maxLength(500)]],
      sequence: [null as number | null],
      padding: [6, [Validators.required, Validators.min(0)]]
    });

    this.companyForm.get('type')?.valueChanges.subscribe(type => {
      if (type === CompanyTypeEnum.PERSON) {
        this.companyForm.get('firstName')?.setValidators([Validators.required, Validators.maxLength(150)]);
        this.companyForm.get('lastName')?.setValidators([Validators.required, Validators.maxLength(150)]);
      } else {
        this.companyForm.get('firstName')?.clearValidators();
        this.companyForm.get('lastName')?.clearValidators();
      }
      this.companyForm.get('firstName')?.updateValueAndValidity();
      this.companyForm.get('lastName')?.updateValueAndValidity();
    });

    this.addressForm.get('addressCountry')?.valueChanges.subscribe((code) => {
      const isMexico = this.isMexicoCountryCode(code);
      this.mexicoSelected.set(isMexico);
      if (!isMexico) {
        this.coloniaOptions.set([]);
      }
      this.cdr.markForCheck();
    });

    this.countryService.getCountries({}, { page: 0, size: 300 }).subscribe({
      next: (r) => this.countries.set(r.body ?? []),
      error: () => this.countries.set([])
    });

    this.taxRegimeService.getAllTaxRegimes().subscribe({
      next: (r) => this.taxRegimeOptions.set(r.body ?? []),
      error: () => this.taxRegimeOptions.set([])
    });
  }

  ngOnInit(): void {
    this.authService.token$.pipe(take(1)).subscribe(() => this.resolveCompany());
  }

  /**
   * Resuelve la empresa del usuario autenticado.
   * Usa GET /companies/me para cargar la empresa (el backend la obtiene del usuario).
   */
  private resolveCompany(): void {
    this.initialLoad.set(true);
    this.loading.set(true);

    console.log('getToken', this.authService.getToken());

    const hasToken = !!this.authService.getToken();
    if (hasToken) {
      this.companyService.getMyCompany().subscribe({
        next: (response) => {
          if (response.ok && response.body) {
            this.isEditMode.set(true);
            this.editingCompanyId.set(response.body.id);
            this.patchCompany(response.body);
            this.addresses.set(response.body.addresses ?? []);
            this.contacts.set(response.body.contacts ?? []);
            this.loadSeries();
          }
          this.loading.set(false);
          this.initialLoad.set(false);
        },
        error: (err) => {
          if (err?.status !== 404) {
            this.toastr.error('Error al cargar la empresa');
          }
          this.showCreateForm();
        }
      });
    } else {
      this.showCreateForm();
    }
  }

  private showCreateForm(): void {
    this.isEditMode.set(false);
    this.editingCompanyId.set(null);
    this.companyForm.reset({
      type: CompanyTypeEnum.COMPANY,
      taxRegimeId: null,
      taxId: '',
      legalName: '',
      comercialName: '',
      firstName: '',
      lastName: '',
      countryCode: null,
      email: '',
      phone: '',
      state: 'ACTIVE',
      daysUntilExpiry: null
    });
    this.addresses.set([]);
    this.contacts.set([]);
    this.series.set([]);
    this.loading.set(false);
    this.initialLoad.set(false);
  }

  private patchCompany(company: CompanyDTO): void {
    const taxRegime = (company as { taxRegime?: TaxRegimeDTO }).taxRegime;
    this.companyForm.patchValue({
      type: company.type,
      taxRegimeId: taxRegime?.id ?? null,
      taxId: company.taxId,
      legalName: company.legalName,
      comercialName: company.comercialName,
      firstName: company.firstName ?? '',
      lastName: company.lastName ?? '',
      countryCode: company.country?.code ?? null,
      email: company.email ?? '',
      phone: company.phone ?? '',
      state: company.state ?? 'ACTIVE',
      daysUntilExpiry: company.daysUntilExpiry ?? null
    });
    if (company.logoUrl) {
      this.logoPreviewUrl.set(company.logoUrl);
      this.logoBase64.set(null);
    }
  }

  editingCompanyIdValue(): number | null {
    return this.editingCompanyId();
  }

  startEditAddress(address: CompanyAddressDTO): void {
    this.editingAddressId.set(address.id);
    this.showAddressForm.set(true);
    this.addressModalShown.set(false);
    const colonia = address.colonia ?? '';
    this.coloniaOptions.set(colonia ? [{ label: colonia, value: colonia }] : []);
    const countryCode = address.country?.code ?? this.findMexicoCountryCode();
    this.mexicoSelected.set(this.isMexicoCountryCode(countryCode));
    this.addressForm.patchValue({
      addressType: address.type,
      addressCountry: countryCode,
      street: address.street ?? '',
      zipCode: address.zipCode ?? '',
      municipality: address.municipality ?? '',
      colonia,
      externalNo: address.externalNo ?? '',
      internalNo: address.internalNo ?? ''
    });
    this.refreshMunicipalityDisplay();
    setTimeout(() => this.addressModalShown.set(true), 10);
  }

  startEditContact(contact: CompanyContactDTO): void {
    this.editingContactId.set(contact.id);
    this.showContactForm.set(true);
    this.contactModalShown.set(false);
    this.contactForm.patchValue({
      contactType: contact.type,
      conactName: contact.conactName,
      contactEmail: contact.email ?? '',
      contactPhone: contact.phone ?? ''
    });
    setTimeout(() => this.contactModalShown.set(true), 10);
  }

  cancelAddressEdit(): void {
    this.addressModalShown.set(false);
    setTimeout(() => {
      this.editingAddressId.set(null);
      this.showAddressForm.set(false);
      this.coloniaOptions.set([]);
      this.addressForm.reset(this.emptyAddressFormValue());
    }, 200);
  }

  cancelContactEdit(): void {
    this.contactModalShown.set(false);
    setTimeout(() => {
      this.editingContactId.set(null);
      this.showContactForm.set(false);
      this.contactForm.reset({
        contactType: 'PRIMARY',
        conactName: '',
        contactEmail: '',
        contactPhone: ''
      });
    }, 200);
  }

  newAddress(): void {
    this.editingAddressId.set(null);
    this.showAddressForm.set(true);
    this.addressModalShown.set(false);
    this.coloniaOptions.set([]);
    this.addressForm.reset(this.emptyAddressFormValue());
    setTimeout(() => this.addressModalShown.set(true), 10);
  }

  newContact(): void {
    this.editingContactId.set(null);
    this.showContactForm.set(true);
    this.contactModalShown.set(false);
    this.contactForm.reset({
      contactType: 'PRIMARY',
      conactName: '',
      contactEmail: '',
      contactPhone: ''
    });
    setTimeout(() => this.contactModalShown.set(true), 10);
  }

  loadSeries(): void {
    if (!this.editingCompanyId()) {
      this.series.set([]);
      return;
    }
    this.seriesLoading.set(true);
    this.serieService.getSeries({}, { page: 0, size: 100, sort: ['serie,asc'] }).pipe(
      finalize(() => {
        this.seriesLoading.set(false);
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (response) => {
        if (response.ok) {
          this.series.set(response.body ?? []);
        } else {
          this.toastr.error(response.message ?? 'Error al cargar series');
        }
      },
      error: () => this.toastr.error('Error al cargar series')
    });
  }

  newSerie(): void {
    this.editingSerieId.set(null);
    this.showSerieForm.set(true);
    this.serieModalShown.set(false);
    this.serieForm.reset({
      type: null,
      serie: '',
      description: '',
      sequence: null,
      padding: 6
    });
    this.serieForm.get('sequence')?.enable({ emitEvent: false });
    setTimeout(() => this.serieModalShown.set(true), 10);
  }

  startEditSerie(serie: SerieDTO): void {
    this.editingSerieId.set(serie.id);
    this.showSerieForm.set(true);
    this.serieModalShown.set(false);
    this.serieForm.patchValue({
      type: serie.type ?? null,
      serie: serie.serie ?? '',
      description: serie.description ?? '',
      sequence: serie.sequence ?? null,
      padding: serie.padding ?? 6
    });
    this.serieForm.get('sequence')?.disable({ emitEvent: false });
    setTimeout(() => this.serieModalShown.set(true), 10);
  }

  cancelSerieEdit(): void {
    this.serieModalShown.set(false);
    setTimeout(() => {
      this.editingSerieId.set(null);
      this.showSerieForm.set(false);
      this.serieForm.reset({
        type: null,
        serie: '',
        description: '',
        sequence: null,
        padding: 6
      });
      this.serieForm.get('sequence')?.enable({ emitEvent: false });
    }, 200);
  }

  saveSerie(): void {
    if (this.serieForm.invalid) {
      this.serieForm.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }
    this.relationsLoading.set(true);
    const formValue = this.serieForm.getRawValue();
    const serieId = this.editingSerieId();

    if (serieId) {
      const request: UpdateSerieRequest = {
        type: formValue.type,
        serie: formValue.serie?.trim() ?? '',
        description: formValue.description || undefined,
        padding: formValue.padding
      };
      this.serieService.updateSerie(serieId, request).pipe(
        finalize(() => this.relationsLoading.set(false))
      ).subscribe({
        next: (response) => {
          if (response.ok && response.body) {
            this.updateSerieList(response.body);
            this.serieModalShown.set(false);
            setTimeout(() => this.cancelSerieEdit(), 200);
            this.toastr.success('Serie actualizada correctamente');
          } else {
            this.toastr.error(response.message ?? 'Error al actualizar la serie');
          }
        },
        error: (err) => this.toastr.error(err.error?.message ?? 'Error al actualizar la serie')
      });
    } else {
      const request: CreateSerieRequest = {
        type: formValue.type,
        serie: formValue.serie?.trim() ?? '',
        description: formValue.description || undefined,
        sequence: formValue.sequence ?? undefined,
        padding: formValue.padding ?? 6
      };
      this.serieService.createSerie(request).pipe(
        finalize(() => this.relationsLoading.set(false))
      ).subscribe({
        next: (response) => {
          if (response.ok && response.body) {
            this.updateSerieList(response.body);
            this.serieModalShown.set(false);
            setTimeout(() => this.cancelSerieEdit(), 200);
            this.toastr.success('Serie creada correctamente');
          } else {
            this.toastr.error(response.message ?? 'Error al crear la serie');
          }
        },
        error: (err) => this.toastr.error(err.error?.message ?? 'Error al crear la serie')
      });
    }
  }

  private updateSerieList(serie: SerieDTO): void {
    this.series.update((items) => {
      const idx = items.findIndex((item) => item.id === serie.id);
      if (idx >= 0) {
        const next = [...items];
        next[idx] = serie;
        return next;
      }
      return [...items, serie];
    });
  }

  serieLabel(serie: SerieDTO): string {
    return serie.serie?.trim() ? serie.serie : 'Sin serie';
  }

  typeLabel(type?: SerieType | null): string {
    if (!type) return '—';
    return this.serieTypeOptions.find(o => o.value === type)?.label ?? type;
  }

  getTypeSeverity(type?: SerieType | null): 'success' | 'info' | 'warn' | 'secondary' | 'primary' {
    switch (type) {
      case 'SALE':
        return 'info';
      case 'SUBSCRIPTION':
        return 'success';
      case 'INVOICE':
        return 'warn';
      case 'SALE_LINK':
        return 'secondary';
      case 'MIT_MANDATE':
        return 'primary';
      default:
        return 'secondary';
    }
  }

  getSerieStateSeverity(state: string): 'success' | 'danger' | 'secondary' {
    if (state === 'ACTIVE') return 'success';
    if (state === 'INACTIVE') return 'danger';
    return 'secondary';
  }

  openDeleteSerie(serie: SerieDTO): void {
    this.deleteTarget.set('serie');
    this.deleteSerieItem.set(serie);
    this.deleteAddressItem.set(null);
    this.deleteContactItem.set(null);
    this.deleteConfirmOpen.set(true);
    this.deleteConfirmShown.set(false);
    setTimeout(() => this.deleteConfirmShown.set(true), 10);
  }

  private emptyAddressFormValue(): Record<string, string> {
    const country = this.findMexicoCountryCode();
    this.mexicoSelected.set(this.isMexicoCountryCode(country));
    this.municipalityDisplay.set('');
    return {
      addressType: 'BILLING',
      addressCountry: country,
      street: '',
      zipCode: '',
      municipality: '',
      colonia: '',
      externalNo: '',
      internalNo: ''
    };
  }

  private refreshMunicipalityDisplay(): void {
    this.municipalityDisplay.set(String(this.addressForm.get('municipality')?.value ?? '').trim());
  }

  private findMexicoCountry(countries: CountryDTO[] = this.countries()): CountryDTO | undefined {
    return countries.find((c) => this.isMexicoCountryCode(c.code) || this.isMexicoCountryName(c.name));
  }

  private findMexicoCountryCode(countries: CountryDTO[] = this.countries()): string {
    return this.findMexicoCountry(countries)?.code ?? 'MX';
  }

  isMexicoCountryCode(code: string | null | undefined): boolean {
    const normalized = (code ?? '').toUpperCase();
    return normalized === 'MX' || normalized === 'MEX';
  }

  private isMexicoCountryName(name: string | null | undefined): boolean {
    const normalized = (name ?? '').toLowerCase();
    return normalized === 'méxico' || normalized === 'mexico';
  }

  isMexicoSelected(): boolean {
    return this.mexicoSelected();
  }

  formatAddressStreet(address: CompanyAddressDTO): string {
    const parts = [
      address.street,
      address.externalNo ? `Ext. ${address.externalNo}` : '',
      address.internalNo ? `Int. ${address.internalNo}` : '',
      address.colonia
    ].filter((part) => !!part && String(part).trim().length > 0);
    return parts.length ? parts.join(', ') : '-';
  }

  formatAddressLocality(address: CompanyAddressDTO): string {
    const parts = [
      address.municipality || '-',
      address.zipCode || ''
    ].filter((part) => !!part && String(part).trim().length > 0);
    return parts.join(', ');
  }

  getAddressTypeLabel(type: string): string {
    return this.addressTypes.find(o => o.value === type)?.label ?? type ?? '';
  }

  getAddressTypeIcon(type: string): string {
    const icons: Record<string, string> = {
      PRIMARY: 'pi-home',
      BILLING: 'pi-credit-card',
      SHIPPING: 'pi-truck',
      CONTACT: 'pi-user',
      OTHER: 'pi-map-marker'
    };
    return icons[type ?? ''] ?? 'pi-map-marker';
  }

  getContactTypeLabel(type: string): string {
    return this.contactTypes.find(o => o.value === type)?.label ?? type ?? '';
  }

  getContactTypeIcon(type: string): string {
    const icons: Record<string, string> = {
      PRIMARY: 'pi-user',
      OFFICE: 'pi-building',
      HOME: 'pi-home',
      MOBILE: 'pi-phone',
      FAX: 'pi-print',
      EMAIL: 'pi-envelope',
      WEBSITE: 'pi-globe',
      SOCIAL_MEDIA: 'pi-share-alt',
      OTHER: 'pi-ellipsis-v'
    };
    return icons[type ?? ''] ?? 'pi-user';
  }

  openDeleteAddress(address: CompanyAddressDTO): void {
    this.deleteTarget.set('address');
    this.deleteAddressItem.set(address);
    this.deleteContactItem.set(null);
    this.deleteSerieItem.set(null);
    this.deleteConfirmOpen.set(true);
    this.deleteConfirmShown.set(false);
    setTimeout(() => this.deleteConfirmShown.set(true), 10);
  }

  openDeleteContact(contact: CompanyContactDTO): void {
    this.deleteTarget.set('contact');
    this.deleteContactItem.set(contact);
    this.deleteAddressItem.set(null);
    this.deleteSerieItem.set(null);
    this.deleteConfirmOpen.set(true);
    this.deleteConfirmShown.set(false);
    setTimeout(() => this.deleteConfirmShown.set(true), 10);
  }

  closeDeleteConfirm(): void {
    this.deleteConfirmShown.set(false);
    setTimeout(() => {
      this.deleteTarget.set(null);
      this.deleteAddressItem.set(null);
      this.deleteContactItem.set(null);
      this.deleteSerieItem.set(null);
      this.deleteConfirmOpen.set(false);
    }, 200);
  }

  confirmDelete(): void {
    const companyId = this.editingCompanyId();
    if (!companyId && this.deleteTarget() !== 'serie') return;
    const target = this.deleteTarget();
    const addressItem = this.deleteAddressItem();
    const contactItem = this.deleteContactItem();
    const serieItem = this.deleteSerieItem();
    this.deleteLoading.set(true);
    if (target === 'address' && addressItem && companyId) {
      this.companyService.deleteCompanyAddress(companyId, addressItem.id).pipe(
        finalize(() => this.deleteLoading.set(false))
      ).subscribe({
        next: (res) => {
          if (res?.ok !== false) {
            this.addresses.update(list => list.filter(a => a.id !== addressItem.id));
            this.closeDeleteConfirm();
            this.toastr.success('Dirección eliminada correctamente');
          } else {
            this.toastr.error(res?.message ?? 'Error al eliminar la dirección');
          }
        },
        error: () => this.toastr.error('Error al eliminar la dirección')
      });
    } else if (target === 'contact' && contactItem && companyId) {
      this.companyService.deleteCompanyContact(companyId, contactItem.id).pipe(
        finalize(() => this.deleteLoading.set(false))
      ).subscribe({
        next: (res) => {
          if (res?.ok !== false) {
            this.contacts.update(list => list.filter(c => c.id !== contactItem.id));
            this.closeDeleteConfirm();
            this.toastr.success('Contacto eliminado correctamente');
          } else {
            this.toastr.error(res?.message ?? 'Error al eliminar el contacto');
          }
        },
        error: () => this.toastr.error('Error al eliminar el contacto')
      });
    } else if (target === 'serie' && serieItem) {
      this.serieService.deleteSerie(serieItem.id).pipe(
        finalize(() => this.deleteLoading.set(false))
      ).subscribe({
        next: (res) => {
          if (res?.ok !== false) {
            this.series.update(list => list.filter(s => s.id !== serieItem.id));
            this.closeDeleteConfirm();
            this.toastr.success('Serie eliminada correctamente');
          } else {
            this.toastr.error(res?.message ?? 'Error al eliminar la serie');
          }
        },
        error: (err) => this.toastr.error(err.error?.message ?? 'Error al eliminar la serie')
      });
    } else {
      this.deleteLoading.set(false);
    }
  }

  saveAddress(): void {
    const companyId = this.editingCompanyId();
    if (!companyId || this.addressForm.invalid) {
      this.addressForm.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }
    this.relationsLoading.set(true);
    const v = this.addressForm.value;
    const payload: CreateCompanyAddressRequest = {
      type: v.addressType,
      country: v.addressCountry,
      street: v.street,
      municipality: v.municipality || undefined,
      colonia: v.colonia || undefined,
      externalNo: v.externalNo || undefined,
      internalNo: v.internalNo || undefined,
      zipCode: v.zipCode || undefined
    };
    const addressId = this.editingAddressId();
    const request$ = addressId
      ? this.companyService.updateCompanyAddress(companyId, addressId, payload)
      : this.companyService.createCompanyAddress(companyId, payload);
    request$.pipe(finalize(() => this.relationsLoading.set(false))).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.updateAddressList(response.body);
          this.addressModalShown.set(false);
          setTimeout(() => this.cancelAddressEdit(), 200);
          this.toastr.success('Dirección guardada correctamente');
        } else {
          this.toastr.error(response.message ?? 'Error al guardar la dirección');
        }
      },
      error: (err) => {
        this.toastr.error(err.error?.message ?? 'Error al guardar la dirección');
      }
    });
  }

  /** Solo 5 dígitos en el código postal. */
  onZipCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, this.maxLength.zipCode);
    if (input.value !== digits) {
      input.value = digits;
    }
    this.addressForm.get('zipCode')?.setValue(digits, { emitEvent: false });
  }

  /** Consulta CP (solo México) y precarga municipio/colonias. */
  onZipCodeBlur(): void {
    if (!this.isMexicoSelected()) {
      return;
    }
    const zipCode = String(this.addressForm.get('zipCode')?.value ?? '').trim();
    if (!/^\d{5}$/.test(zipCode)) {
      return;
    }

    this.postalCodeLoading.set(true);
    this.addressService.getByZipCode(zipCode).pipe(
      finalize(() => {
        this.postalCodeLoading.set(false);
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (response) => {
        const data = response.body;
        if (!response.ok || !data) {
          return;
        }

        const neighborhoods = (data.neighborhoods ?? [])
          .map((n) => ({ label: n.name ?? '', value: n.name ?? '' }))
          .filter((n) => n.value);
        this.coloniaOptions.set(neighborhoods);

        const currentColonia = this.addressForm.get('colonia')?.value as string;
        const colonia =
          neighborhoods.length === 1
            ? neighborhoods[0].value
            : neighborhoods.some((n) => n.value === currentColonia)
              ? currentColonia
              : '';

        this.addressForm.patchValue({
          municipality: data.municipality ?? '',
          colonia
        });
        this.refreshMunicipalityDisplay();
      },
      error: () => {
        // Sin resultados o error: no mostrar toast ni alterar el formulario.
      }
    });
  }

  saveContact(): void {
    const companyId = this.editingCompanyId();
    if (!companyId || this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }
    this.relationsLoading.set(true);
    const v = this.contactForm.value;
    const phoneDigits = typeof v.contactPhone === 'string' ? v.contactPhone.replace(/\D/g, '').slice(0, 10) : '';
    const payload: CreateCompanyContactRequest = {
      type: v.contactType,
      conactName: v.conactName,
      email: v.contactEmail || undefined,
      phone: phoneDigits || undefined
    };
    const contactId = this.editingContactId();
    const request$ = contactId
      ? this.companyService.updateCompanyContact(companyId, contactId, payload)
      : this.companyService.createCompanyContact(companyId, payload);
    request$.pipe(finalize(() => this.relationsLoading.set(false))).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.updateContactList(response.body);
          this.contactModalShown.set(false);
          setTimeout(() => this.cancelContactEdit(), 200);
          this.toastr.success('Contacto guardado correctamente');
        } else {
          this.toastr.error(response.message ?? 'Error al guardar el contacto');
        }
      },
      error: (err) => {
        this.toastr.error(err.error?.message ?? 'Error al guardar el contacto');
      }
    });
  }

  private updateAddressList(address: CompanyAddressDTO): void {
    this.addresses.update((items) => {
      const idx = items.findIndex((item) => item.id === address.id);
      if (idx >= 0) {
        const next = [...items];
        next[idx] = address;
        return next;
      }
      return [...items, address];
    });
  }

  private updateContactList(contact: CompanyContactDTO): void {
    this.contacts.update((items) => {
      const idx = items.findIndex((item) => item.id === contact.id);
      if (idx >= 0) {
        const next = [...items];
        next[idx] = contact;
        return next;
      }
      return [...items, contact];
    });
  }

  onSubmit(): void {
    if (this.companyForm.invalid) {
      this.companyForm.markAllAsTouched();
      return;
    }
    const formValue = this.companyForm.value;
    const countryCode = formValue.countryCode as string;
    const taxRegimeId = formValue.taxRegimeId as number;
    if (!countryCode) {
      this.companyForm.get('countryCode')?.markAsTouched();
      return;
    }
    if (taxRegimeId == null || taxRegimeId === undefined) {
      this.companyForm.get('taxRegimeId')?.markAsTouched();
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    const logoB64 = this.logoBase64();
    const request: CreateCompanyRequest = {
      type: formValue.type,
      taxRegimeId,
      taxId: formValue.taxId,
      legalName: formValue.legalName,
      comercialName: formValue.comercialName,
      firstName: formValue.firstName || undefined,
      lastName: formValue.lastName || undefined,
      country: countryCode,
      email: formValue.email || undefined,
      phone: formValue.phone || undefined,
      state: formValue.state ?? 'ACTIVE',
      logoBase64: logoB64 ?? undefined,
      logoContentType: logoB64 ? this.logoContentType() : undefined,
      daysUntilExpiry: formValue.daysUntilExpiry ?? null
    };

    const companyId = this.editingCompanyId();
    if (companyId != null) {
      this.companyService.updateCompany(companyId, request).pipe(
        finalize(() => this.loading.set(false))
      ).subscribe({
        next: (response) => {
          if (response.ok && response.body) {
            this.patchCompany(response.body);
            this.logoBase64.set(null);
            this.toastr.success('Empresa actualizada correctamente');
          } else {
            this.toastr.error(response.message ?? 'Error al actualizar la empresa');
          }
        },
        error: (err) => {
          this.toastr.error(err.error?.message ?? 'Error al actualizar la empresa');
        }
      });
    } else {
      this.companyService.createCompany(request).pipe(
        finalize(() => this.loading.set(false))
      ).subscribe({
        next: (response) => {
          if (response.ok && response.body) {
            this.isEditMode.set(true);
            this.editingCompanyId.set(response.body.id);
            this.patchCompany(response.body);
            this.logoBase64.set(null);
            this.addresses.set(response.body.addresses ?? []);
            this.contacts.set(response.body.contacts ?? []);
            this.loadSeries();
            this.toastr.success('Empresa creada correctamente');
          } else {
            this.toastr.error(response.message ?? 'Error al crear la empresa');
          }
        },
        error: (err) => {
          this.toastr.error(err.error?.message ?? 'Error al crear la empresa');
        }
      });
    }
  }

  cancel(): void {
    this.router.navigate(['/forecasts/sales-operations']);
  }

  statusDisplayLabel(): string {
    const state = this.companyForm.get('state')?.value;
    return this.stateOptions.find((o) => o.value === state)?.label ?? (state || '—');
  }

  // === Logo upload methods ===

  onLogoFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.toastr.error('Por favor selecciona una imagen válida');
      return;
    }
    const MAX_SIZE_MB = 1;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      this.toastr.error(`La imagen no puede superar ${MAX_SIZE_MB} MB (tamaño actual: ${(file.size / 1024 / 1024).toFixed(2)} MB)`);
      return;
    }
    this.logoContentType.set(file.type);
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      this.logoPreviewUrl.set(result);
      
      // Extract base64 content
      const base64Data = result.split(',')[1];
      this.logoBase64.set(base64Data);
    };
    reader.readAsDataURL(file);
  }

  removeLogo(): void {
    this.logoPreviewUrl.set(null);
    this.logoBase64.set(null);
  }
}
